// Implements: F-IDN-014, F-UIX-009
/**
 * Enrolling the second factor (v3 `acctTotp`, state `enrolling`): first the current password,
 * then the QR with its key spelled in groups of four and the six-digit code. Pure presentation:
 * the password and the code go up through `onPassword` and `onVerify`; the enrolment (secret and
 * URI) arrives as a prop and is never stored here beyond the render.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { TotpEnrolment } from "../../../types/api";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Input } from "../../../shared/ui/atoms/Input";
import { RateLimitWait } from "../../../shared/ui/molecules/RateLimitWait";
import { CODE_LENGTH, TotpCodeInput } from "./TotpCodeInput";
import { TotpHeader } from "./TotpHeader";
import { TotpQr } from "./TotpQr";
import styles from "./TotpEnrolSteps.module.css";
import shared from "./account.module.css";

/** "JBSWY3DPEHPK3PXP7Q2M" as "JBSW Y3DP EHPK 3PXP 7Q2M", easier to type by hand. */
export function spacedSecret(secret: string): string {
  return (secret.match(/.{1,4}/g) ?? []).join(" ");
}

export type CodeError = "missing" | "wrong" | null;

export interface TotpEnrolStepsProps {
  /** `null` while the password is being asked. */
  readonly enrolment: TotpEnrolment | null;
  readonly busy: boolean;
  /** `true` while the code is being checked: cancelling then would race the activation. */
  readonly cancelDisabled: boolean;
  readonly wrongPassword: boolean;
  readonly codeError: CodeError;
  readonly waitSeconds: number | null;
  readonly onPassword: (password: string) => void;
  readonly onVerify: (code: string) => void;
  readonly onCancel: () => void;
  readonly onCopy: (secret: string) => void;
}

export function TotpEnrolSteps(props: TotpEnrolStepsProps): ReactElement {
  const { enrolment, busy, cancelDisabled, wrongPassword, codeError, waitSeconds, onPassword, onVerify, onCancel, onCopy } =
    props;
  const { t } = useTranslation();
  const ids = { title: useId(), password: useId(), passwordError: useId(), codeError: useId() };
  const passwordRef = useRef<HTMLInputElement>(null);
  const firstDigit = useRef<HTMLInputElement>(null);
  const [password, setPassword] = useState("");
  const [passwordMissing, setPasswordMissing] = useState(false);
  const [code, setCode] = useState("");
  const [codeMissing, setCodeMissing] = useState(false);

  // Each step takes the focus where the person types next.
  useEffect(() => {
    (enrolment === null ? passwordRef : firstDigit).current?.focus();
  }, [enrolment]);

  const badge = (
    <Badge tone="info">
      <Icon name="loader" size="sm" />
      {t("account.totp.badgeEnrolling")}
    </Badge>
  );
  const cancel = (
    <Button variant="secondary" disabled={cancelDisabled} onClick={onCancel}>
      {t("account.totp.cancel")}
    </Button>
  );
  const wait = waitSeconds === null ? null : <RateLimitWait seconds={waitSeconds} />;

  if (enrolment === null) {
    const passwordError = passwordMissing
      ? t("account.totp.passwordRequired")
      : wrongPassword
        ? t("account.password.wrongCurrent")
        : null;
    return (
      <form
        className={shared.card}
        aria-labelledby={ids.title}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (password === "") {
            setPasswordMissing(true);
            passwordRef.current?.focus();
            return;
          }
          onPassword(password);
        }}
      >
        <TotpHeader titleId={ids.title} badge={badge} />
        <div className={shared.body}>
          <p className={shared.text}>{t("account.totp.confirmPassword")}</p>
          {wait}
          <div className={shared.field}>
            <label className={shared.label} htmlFor={ids.password}>
              {t("account.password.current")}
            </label>
            <Input
              id={ids.password}
              type="password"
              value={password}
              autoComplete="current-password"
              inputRef={passwordRef}
              invalid={passwordError !== null}
              describedBy={passwordError === null ? undefined : ids.passwordError}
              onChange={(value) => {
                setPassword(value);
                setPasswordMissing(false);
              }}
            />
            {passwordError === null ? null : (
              <p className={shared.error} id={ids.passwordError} role="alert">
                {passwordError}
              </p>
            )}
          </div>
        </div>
        <div className={shared.foot}>
          {cancel}
          <Button variant="primary" type="submit" loading={busy} disabled={busy || waitSeconds !== null}>
            {t("account.totp.continue")}
          </Button>
        </div>
      </form>
    );
  }

  const codeMessage = codeMissing ? t("account.totp.codeMissing") : codeError === "wrong" ? t("account.totp.codeWrong") : null;
  return (
    <form
      className={[shared.card, shared.wide].join(" ")}
      aria-labelledby={ids.title}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (code.length !== CODE_LENGTH) {
          setCodeMissing(true);
          firstDigit.current?.focus();
          return;
        }
        setCodeMissing(false);
        onVerify(code);
      }}
    >
      <TotpHeader titleId={ids.title} badge={badge} />
      <div className={shared.body}>
        {wait}
        <ol className={styles.steps}>
          <li>
            <div>
              <b className={styles.stepTitle}>{t("account.totp.scan")}</b>
              <p className={shared.help}>{t("account.totp.scanHelp")}</p>
              <div className={styles.enrol}>
                <TotpQr uri={enrolment.otpauth_uri} label={t("account.totp.qrLabel")} />
                <div className={shared.field}>
                  <span className={shared.label}>{t("account.totp.secretLabel")}</span>
                  <div className={styles.secretRow}>
                    <code className={styles.secret}>{spacedSecret(enrolment.secret_base32)}</code>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        onCopy(enrolment.secret_base32);
                      }}
                    >
                      <Icon name="copy" size="sm" />
                      {t("account.totp.copy")}
                    </Button>
                  </div>
                  <p className={shared.noticeInfo}>
                    <Icon name="lock" size="sm" />
                    <span>{t("account.totp.secretOnce")}</span>
                  </p>
                </div>
              </div>
            </div>
          </li>
          <li>
            <div>
              <b className={styles.stepTitle}>{t("account.totp.typeCode")}</b>
              <TotpCodeInput
                value={code}
                firstRef={firstDigit}
                invalid={codeMessage !== null}
                describedBy={codeMessage === null ? undefined : ids.codeError}
                disabled={busy}
                onChange={(value) => {
                  setCode(value);
                  setCodeMissing(false);
                }}
              />
              {codeMessage === null ? null : (
                <p className={shared.error} id={ids.codeError} role="alert">
                  {codeMessage}
                </p>
              )}
            </div>
          </li>
        </ol>
      </div>
      <div className={shared.foot}>
        {cancel}
        <Button variant="primary" type="submit" loading={busy} disabled={busy || waitSeconds !== null}>
          {t("account.totp.verify")}
        </Button>
      </div>
    </form>
  );
}
